'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import bodyImage from '../../assets/images/body.svg';
import mindImage from '../../assets/images/mind.svg';
import sleepImage from '../../assets/images/sleep.svg';
import styles from './ColorsSection.module.css';

const lines = [
  {
    id: 10,
    text: 'Progress everyday with carefully-crafted workout programs',
  },
  {
    id: 20,
    text: 'Discover the power of your mind via research-backed meditation',
  },
  { id: 30, text: 'Fall asleep naturally with our bedtime stories' },
];

const getTranslateGivenProgressionInViewPort = ({
  progression,
  lineIndex,
}: {
  progression: number;
  lineIndex: number;
}) => {
  const maxTransform = 0.2;
  const duration = 1 / lines.length;
  const startBreakpoint = duration * lineIndex;
  const endBreakpoint = startBreakpoint + duration;

  if (progression < startBreakpoint) {
    return maxTransform;
  }

  const newProgression =
    (progression - startBreakpoint) / (endBreakpoint - startBreakpoint);
  const value = maxTransform - maxTransform * newProgression;

  return Math.min(Math.max(-maxTransform, value), maxTransform);
};

const getOpacityGivenProgressionInViewPort = ({
  progression,
  lineIndex,
}: {
  progression: number;
  lineIndex: number;
}) => {
  const duration = 1 / lines.length;
  const startBreakpoint = (1 / lines.length) * lineIndex;
  const endBreakpoint = startBreakpoint + duration;
  if (progression < startBreakpoint) {
    return 0;
  }
  const newProgression =
    (progression - startBreakpoint) / (endBreakpoint - startBreakpoint);

  if (progression > endBreakpoint) {
    return Math.max(0, 1 - (newProgression - 1) * 4);
  }
  return Math.min(newProgression, 1);
};

const getTransformForColor = ({
  progression,
  colorIndex,
}: {
  progression: number;
  colorIndex: number;
}) => {
  const desktopPositions = [
    { x: 0, y: -8 },
    { x: -25, y: 53 },
    { x: 25, y: 53 },
  ];
  const mobilesPositions = [
    { x: 0, y: -25 },
    { x: 0, y: 55 },
    { x: 0, y: 55 },
  ];

  const positions =
    window.innerWidth < 1024 ? mobilesPositions : desktopPositions;

  const phase =
    (progression - (progression % (1 / lines.length))) / (1 / lines.length);

  const prevPositionIndex =
    progression < 1 / positions.length
      ? null
      : (phase + colorIndex - 1) % positions.length;
  const newPositionIndex =
    progression < 1 / positions.length
      ? (phase + colorIndex) % positions.length
      : ((prevPositionIndex || 0) + 1) % positions.length;

  const prevPosition =
    prevPositionIndex !== null
      ? positions[prevPositionIndex]
      : {
          x: 0,
          y: 0,
        };

  const newPosition =
    phase >= lines.length ? { x: 0, y: 0 } : positions[newPositionIndex];

  const speed = phase >= lines.length ? 5 : 1;

  const newProgression = Math.min(
    1,
    ((progression % (1 / lines.length)) / (1 / lines.length)) * speed,
  );

  const x = prevPosition.x + (newPosition.x - prevPosition.x) * newProgression;

  const y = prevPosition.y + (newPosition.y - prevPosition.y) * newProgression;

  return `translate(${x}vw, ${y}vh)`;
};

const getOpacityForColor = ({
  progression,
}: {
  progression: number;
  colorIndex: number;
}) => {
  const startBreakpoint = 0.1;
  const endBreakpoint = startBreakpoint + 0.15;
  if (progression < startBreakpoint) {
    return 0;
  }

  if (progression > 1) {
    return 1 - (progression - 1) * 15;
  }

  const newProgression =
    (progression - startBreakpoint) / (endBreakpoint - startBreakpoint);

  return Math.max(0, Math.min(newProgression, 1));
};

export const ColorsSection = ({ className }: { className?: string }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRefs = useRef<
    ReadonlyArray<React.MutableRefObject<HTMLDivElement | null>>
  >(lines.map(() => React.createRef<HTMLDivElement | null>()));
  const colorRefs = useRef<
    ReadonlyArray<React.MutableRefObject<HTMLDivElement | null>>
  >([1, 2, 3].map(() => React.createRef<HTMLDivElement | null>()));
  const latestAnimationFrame = useRef<number | null>(null);
  const intersectionObserver = useRef<IntersectionObserver | null>(null);

  const animate = useCallback(() => {
    if (containerRef.current) {
      const containerBoundingRect =
        containerRef.current.getBoundingClientRect();
      const posTopContainerToViewport = containerBoundingRect.top;
      const height = Math.max(containerBoundingRect.height, window.innerHeight);
      const progression = Math.max(
        0,
        -(posTopContainerToViewport - window.innerHeight) / height,
      );

      contentRefs.current.forEach((contentRef, lineIndex) => {
        if (!contentRef.current) {
          return;
        }
        const opacity = getOpacityGivenProgressionInViewPort({
          progression,
          lineIndex,
        });
        contentRef.current.style.transform = `translateY(${
          getTranslateGivenProgressionInViewPort({ progression, lineIndex }) *
          100
        }vh)`;
        contentRef.current.style.opacity = `${opacity}`;
        if (opacity > 0.3) {
          contentRef.current.classList.add(styles.active);
        } else {
          contentRef.current.classList.remove(styles.active);
        }
      });

      colorRefs.current.forEach((colorRef, colorIndex) => {
        if (!colorRef.current) {
          return;
        }
        colorRef.current.style.transform = getTransformForColor({
          progression,
          colorIndex,
        });
        colorRef.current.style.opacity = `${getOpacityForColor({
          progression,
          colorIndex,
        })}`;
      });
    }
    latestAnimationFrame.current = requestAnimationFrame(animate);
  }, []);

  const startAnimation: IntersectionObserverCallback = (entries) => {
    const entry = entries[0];

    if (entry && entry.isIntersecting && !latestAnimationFrame.current) {
      latestAnimationFrame.current = requestAnimationFrame(animate);
      return;
    }
    if (latestAnimationFrame.current) {
      cancelAnimationFrame(latestAnimationFrame.current);
    }
    latestAnimationFrame.current = null;
  };

  useEffect(() => {
    intersectionObserver.current = new IntersectionObserver(startAnimation, {
      root: null,
      rootMargin: '0px',
      threshold: 0.01,
    });
    if (containerRef.current) {
      intersectionObserver.current.observe(containerRef.current);
    }
    return () => {
      if (latestAnimationFrame.current) {
        cancelAnimationFrame(latestAnimationFrame.current);
      }
      intersectionObserver.current?.disconnect();
    };
  }, []);

  return (
    <div
      className={className ? `${styles.root} ${className}` : styles.root}
      ref={containerRef}
    >
      <div>
        <div
          className={styles.backgroundColor}
          style={{ ['--color-bg-image' as any]: `url(${bodyImage.src})` }}
          ref={colorRefs.current[0]}
        />
        <div
          className={styles.backgroundColor}
          style={{ ['--color-bg-image' as any]: `url(${mindImage.src})` }}
          ref={colorRefs.current[1]}
        />
        <div
          className={styles.backgroundColor}
          style={{ ['--color-bg-image' as any]: `url(${sleepImage.src})` }}
          ref={colorRefs.current[2]}
        />
        {lines.map((line, index) => (
          <div
            className={`${styles.line} u-grid`}
            ref={contentRefs.current[index]}
            key={line.id}
          >
            <div>
              <h2>{line.text}</h2>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
